/** Profil tabi (demo v3 `Profile`, `ProfileEdit`, `achievements`): avatar, nick, unvon, shior, statistika, haftalik diagramma, yutuqlar. */
import { useEffect, useState } from 'react';
import { LN, DAYS } from '../data';
import { FM } from '../data/forms';
import { useStore, S, learnedN, started, streakNow, weekDays, today } from '../store';
import { nav } from '../lib/nav';
import { toast } from '../lib/toast';
import { fire, sync } from '../lib/sync';
import { ApiError } from '../lib/api';
import { nextTitle, titleOf } from '../lib/xp';
import { t, tn } from '../i18n';
import { Ic } from '../ui/Icon';
import { SheetBox } from '../ui/Sheet';
import { FRAMES, MyAvatar } from '../ui/Avatar';
import { obStart } from './Onboarding';
import { DonateCard, FeedbackSheet } from '../ui/Support';

const H = 104;
const fmt = (n: number) => n.toLocaleString('ru');

/** Demo `defNick`: ism (lotin) + _ + 3 raqam. */
const defNick = (name: string) => ((name || 'lexo').toLowerCase().replace(/[^a-z0-9]/g, '') || 'lexo').slice(0, 10) + '_' + (100 + Math.floor(Math.random() * 900));

/** Nick yo'q bo'lsa — yaratib serverga yozadi (band bo'lsa boshqa raqam bilan qayta urinadi). */
async function ensureNick(tries = 3): Promise<void> {
  const s = S();
  if (s.nick) return;
  const nick = defNick(s.name);
  try { await sync.me({ nick }); s.set({ nick }); }
  catch (e) { if (e instanceof ApiError && e.status === 409 && tries > 1) return ensureNick(tries - 1); }
}

/** [nom, izoh, ikonka, joriy qiymat, maqsad] */
export function achievements(): Array<[string, string, string, number, number]> {
  const s = S();
  const fwL = FM.items.filter((x) => (s.fw[x.id]?.box ?? 0) >= 3).length;
  return [
    ['Birinchi qadam', 'Birinchi darsni tugating', 'flag', started().length, 1],
    ['Olov', '7 kunlik streak', 'flame', Math.max(s.bestStreak || 0, streakNow()), 7],
    ['Soʻz ovchisi', '50 ta soʻz yodlang', 'book', learnedN(), 50],
    ['Jangchi', 'Soʻz jangida gʻalaba', 'swords', s.duels?.win ?? 0, 1],
    ['Tez quloq', 'Sonlar · Qiyin: 8/10', 'hash', s.bestNum?.hard ?? 0, 8],
    ['Feʼl ustasi', '15 ta shaklni yodlang', 'layers', fwL, 15],
    ['XP boyvachchasi', '10 000 XP yigʻing', 'zap', s.xp, 10000],
  ];
}

/** Profilni tahrirlash varag'i: ism, nick, shior, avatar ramkasi. */
function ProfileEdit({ onClose }: { onClose: () => void }) {
  const s = useStore();
  const [name, setName] = useState(s.name);
  const [nick, setNick] = useState(s.nick ?? '');
  const [bio, setBio] = useState(s.bio);
  const [fr, setFr] = useState(s.frame ?? 'Yoʻq');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const best = Math.max(s.bestStreak || 0, streakNow());

  const save = async () => {
    const nk = nick.trim();
    if (!/^[a-z0-9_]{3,16}$/.test(nk)) { setErr(t('Nickname 3–16 belgidan iborat boʻlsin: a–z, 0–9, _')); return; }
    const frame = fr === 'Yoʻq' ? '' : fr;
    setBusy(true);
    try {
      await sync.me({ ...(name.trim() ? { name: name.trim() } : {}), nick: nk, bio: bio.trim(), frame });
      S().set({ ...(name.trim() ? { name: name.trim() } : {}), nick: nk, bio: bio.trim(), frame: frame || null });
      onClose(); toast(t('Profil saqlandi'));
    } catch (e) {
      setErr(e instanceof ApiError && e.status === 409 ? t('Bu nickname band — boshqasini tanlang') : t('Saqlab boʻlmadi — internetni tekshiring'));
    } finally { setBusy(false); }
  };

  return (
    <div className="lsh" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <SheetBox title={t('Profilni tahrirlash')} onClose={onClose}>
        <div className="col g10" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          <label className="cap" htmlFor="pn">{t('Ism')}</label>
          <div className="field"><input id="pn" maxLength={24} value={name} onChange={(e) => setName(e.target.value)} /></div>
          <label className="cap" htmlFor="pk">{t('Nickname')}</label>
          <div className="field nickf"><span>@</span><input id="pk" maxLength={16} value={nick} autoCapitalize="off" autoCorrect="off" spellCheck={false}
            onChange={(e) => { setNick(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')); setErr(''); }} /></div>
          <span className="t12" style={err ? { color: 'var(--error-ink)' } : undefined}>{err || t('3–16 belgi: lotin harflari, raqam va _ . Reytingda va janglarda shu nom koʻrinadi.')}</span>
          <label className="cap" htmlFor="pb">{t('Shior (ixtiyoriy)')}</label>
          <div className="field"><input id="pb" maxLength={60} value={bio} placeholder={t('Masalan: Har kuni 10 ta soʻz!')} onChange={(e) => setBio(e.target.value)} /></div>
          <span className="cap">{t('Avatar ramkasi')}</span>
          <div className="frames">
            {FRAMES.map(([n, c, need, cond]) => {
              const lock = (need > 0 && s.xp < need) || (cond === 'streak7' && best < 7);
              return (
                <button key={n} className={`frm ${fr === n ? 'on' : ''}`} disabled={lock} onClick={() => setFr(n)}>
                  <span style={c ? { background: c } : { background: 'var(--bg)', border: '1.5px dashed var(--border)' }} />
                  <small>{lock ? <><Ic n="lock" size={10} />{need ? `${need / 1000}k XP` : t('7 kun')}</> : t(n)}</small>
                </button>
              );
            })}
          </div>
          <button className="btn" style={{ height: 48 }} disabled={busy} onClick={save}>{t('Saqlash')}</button>
        </div>
      </SheetBox>
    </div>
  );
}

export function Profile() {
  const s = useStore(); // butun holatga obuna
  const [edit, setEdit] = useState(false);
  const [fb, setFb] = useState(false);
  useEffect(() => { void ensureNick(); }, []);
  useEffect(() => {
    const st = streakNow();
    if (st > (s.bestStreak || 0)) s.set({ bestStreak: st });
  }, []); // eslint-disable-line
  const tot = s.right + s.wrong, acc = tot ? Math.round(s.right / tot * 100) : 0;
  const wd = weekDays(), tt = today();
  const mx = Math.max(1, ...wd.map((d) => (s.log[d] ? s.log[d]!.n + s.log[d]!.r : 0)));
  const nt = nextTitle(s.xp), ach = achievements(), got = ach.filter((a) => a[3] >= a[4]).length;
  const openSettings = () => nav.push({ k: 'settings' });

  return (
    <>
      <section className="pfcard">
        <div className="row g14">
          <button className="pfav" aria-label={t('Avatarni oʻzgartirish')} onClick={() => nav.push({ k: 'avatar' })}>
            <MyAvatar size={76} /><span className="pfcam"><Ic n="edit" size={14} /></span>
          </button>
          <div className="grow col g4" style={{ minWidth: 0 }}>
            <b style={{ fontSize: 20, lineHeight: '26px', fontWeight: 800 }}>{s.name || t('Lexo foydalanuvchisi')}</b>
            {s.nick && <span className="t13" style={{ fontWeight: 700, color: 'var(--primary-ink)' }}>@{s.nick}</span>}
            <span className="ttl"><Ic n="trophy" size={13} />{t(titleOf(s.xp))}</span>
          </div>
          <button className="sq2 card" aria-label={t('Sozlamalar')} onClick={openSettings}><Ic n="sliders" size={20} /></button>
        </div>
        {s.bio && <p className="pfbio">“{s.bio}”</p>}
        <div className="row g8 wrapr">
          <span className="chipx">{t(LN.name)} · {s.lvl}</span>
          {s.survey?.why && <span className="chipx"><Ic n="target" size={12} />{t(s.survey.why)}</span>}
          <button className="chipx act" onClick={() => setEdit(true)}><Ic n="edit" size={12} />{t('Profilni tahrirlash')}</button>
        </div>
        {nt && (
          <div className="col g4">
            <div className="between t12" style={{ fontWeight: 700 }}><span>{t('Keyingi unvon')}: {t(nt[1])}</span><span>{fmt(s.xp)} / {fmt(nt[0])} XP</span></div>
            <span className="trk2"><i style={{ width: `${Math.min(100, Math.round(s.xp / nt[0] * 100))}%` }} /></span>
          </div>
        )}
      </section>
      <div className="stat3">
        <div><b>{learnedN()}</b><span>{t('Yodlangan')}</span></div>
        <div><b style={{ color: 'var(--peach-ink)' }}>{streakNow()}</b><span>{t('Kunlik streak')}</span></div>
        <div><b style={{ color: 'var(--success-ink)' }}>{acc}%</b><span>{t('Aniqlik')}</span></div>
      </div>
      <section className="card col g14" style={{ padding: 16, borderRadius: 22 }}>
        <div className="between">
          <b className="t15b">{t('Shu hafta')}</b>
          <span className="row g12 t12" style={{ fontWeight: 600 }}>
            <span className="row g4"><i style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--primary)' }} />{t('Yangi')}</span>
            <span className="row g4"><i style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--peach)' }} />{t('Takrorlash')}</span>
          </span>
        </div>
        <div className="bars">
          {wd.map((d) => {
            const L = s.log[d];
            if (!L) return <div key={d}><i style={{ height: 6, background: 'var(--primary-soft)', borderRadius: 3 }} /></div>;
            const hr = Math.round(L.r / mx * H), hn = Math.round(L.n / mx * H);
            return (
              <div key={d} title={t(`${L.n} yangi, ${L.r} takror`)}>
                {L.r > 0 && <i style={{ height: Math.max(4, hr), background: 'var(--peach)' }} />}
                {L.n > 0 && <i style={{ height: Math.max(4, hn), background: 'var(--primary)' }} />}
              </div>
            );
          })}
        </div>
        <div className="dl">{wd.map((d, i) => <span key={d} className={d === tt ? 'now' : ''}>{t(DAYS[i]!)}</span>)}</div>
      </section>
      <div className="between"><b className="t16b" style={{ fontSize: 18 }}>{t('Yutuqlar')}</b><span className="t12" style={{ fontWeight: 800 }}>{got}/{ach.length}</span></div>
      <div className="achv">
        {ach.map(([n, d, i, v, goal]) => {
          const ok = v >= goal;
          return (
            <div key={n} className={`ac ${ok ? 'on' : ''}`} title={t(d)}>
              <span className="aci"><Ic n={ok ? i : 'lock'} size={22} /></span>
              <b>{t(n)}</b>
              <small>{ok ? t(d) : `${fmt(Math.min(v, goal))}/${fmt(goal)}`}</small>
            </div>
          );
        })}
      </div>
      <DonateCard />
      <div className="menu">
        <button onClick={() => setFb(true)}><span className="row g10"><Ic n="msg" size={20} style={{ color: 'var(--primary-ink)' }} />{t('Fikr va takliflar')}</span><Ic n="chevRight" size={20} style={{ color: 'var(--muted)' }} /></button>
        <button onClick={openSettings}><span className="row g10"><Ic n="sliders" size={20} style={{ color: 'var(--primary-ink)' }} />{t('Sozlamalar')}</span><span className="row g6 t12">{LN.code} · {tn('# soʻz/kun', s.goal)}<Ic n="chevRight" size={20} style={{ color: 'var(--muted)' }} /></span></button>
        <button onClick={() => { obStart.step = 1; s.set({ onb: false }); }}><span className="row g10"><Ic n="clip" size={20} style={{ color: 'var(--muted)' }} />{t('Soʻrovnoma javoblarim')}</span><Ic n="chevRight" size={20} style={{ color: 'var(--muted)' }} /></button>
      </div>
      <p className="t12" style={{ margin: 0 }}>{t('Progress Lexo hisobingizda saqlanadi. “Ertangi kunga oʻtish” keyingi kun darsini kutmasdan sinash uchun.')}</p>
      {edit && <ProfileEdit onClose={() => setEdit(false)} />}
      {fb && <div className="lsh" onClick={(e) => { if (e.target === e.currentTarget) setFb(false); }}><FeedbackSheet onClose={() => setFb(false)} /></div>}
    </>
  );
}

/** Avatar saqlash (AvatarEditor'dan): store + server. */
export function saveAvatar(avatar: ReturnType<typeof S>['avatar']) {
  S().set({ avatar });
  fire(sync.me({ avatar }), 'avatar');
}
